import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import BASE_URL from '../../url';
import * as XLSX from 'xlsx';
import './OnboardingReport.css';
import '../components/OnboardingStyles.css';
import defaultOnboardingData from '../../data/onboardingChecklistData.json';
import { parseOnboardingChecklistWorkbook } from '../utils/onboardingExcelParser';
import { 
  RotateCw, 
  Search, 
  AlertTriangle, 
  CheckCircle2, 
  TrendingUp, 
  Building2, 
  Calendar,
  Layers, 
  Users, 
  ShieldCheck, 
  Hourglass, 
  Sparkles, 
  Rocket, 
  CalendarCheck,
  ChevronLeft, 
  ChevronRight,
  X
} from 'lucide-react';

const STATUS_CLASS = {
  "Started": "st-started",
  "Received Start Date": "st-startdate",
  "BGC Completed": "st-done",
  "BGC In Progress": "st-prog",
  "BGC Initiated": "st-init",
  "Offer in Progress": "st-offer",
  "Onboarding In Progress": "st-onboard",
  "Onboarding Completed": "st-onboarddone",
  "Position Confirmed": "st-conf",
  "Position Hold": "st-hold",
  "Hold": "st-hold",
  "Yet to Finalize": "st-fin",
  "Dropped": "st-dropped"
};

const STATUS_COLOR = {
  "Started": "#10b981",
  "Received Start Date": "#d97706",
  "BGC Completed": "#16a34a",
  "BGC In Progress": "#0284c7",
  "BGC Initiated": "#64748b",
  "Offer in Progress": "#db2777",
  "Onboarding In Progress": "#0d9488",
  "Onboarding Completed": "#8b5cf6",
  "Position Confirmed": "#8b5cf6",
  "Position Hold": "#d97706",
  "Hold": "#d97706",
  "Yet to Finalize": "#94a3b8",
  "Dropped": "#ef4444"
};

const getInitials = (name) => {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getAvatarBg = (name) => {
  const colors = [
    'linear-gradient(135deg, #3b5bfd, #2563eb)',
    'linear-gradient(135deg, #3b82f6, #1d4ed8)',
    'linear-gradient(135deg, #60a5fa, #1d4ed8)',
    'linear-gradient(135deg, #8b5cf6, #6d28d9)',
    'linear-gradient(135deg, #f59e0b, #b45309)',
    'linear-gradient(135deg, #ec4899, #be185d)',
    'linear-gradient(135deg, #06b6d4, #0e7490)',
    'linear-gradient(135deg, #6366f1, #4338ca)',
  ];
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash += name.charCodeAt(i);
  return colors[Math.abs(hash) % colors.length];
};

const parseDate = (d) => {
  if (!d) return 0;
  if (typeof d === 'string' && d.includes('/')) {
    const parts = d.split('/').map(Number);
    if (parts.length === 3) {
      return new Date(parts[2], parts[0] - 1, parts[1]).getTime();
    }
  }
  const parsed = new Date(d).getTime();
  return isNaN(parsed) ? 0 : parsed;
};

const formatDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  const y = dt.getFullYear();
  return `${m}/${day}/${y}`;
};

const toYMD = (d) => {
  if (!d) return '';
  const dt = (d instanceof Date) ? d : new Date(d);
  if (isNaN(dt.getTime())) return '';
  const year = dt.getFullYear();
  const month = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Calculate calendar prior week window (Monday 00:00:00 to Sunday 23:59:59)
const getPriorWeekWindow = (refDate = new Date()) => {
  const d = new Date(refDate);
  const day = d.getDay(); // 0 is Sunday, 1 is Monday...
  const diffToMonday = d.getDate() - day + (day === 0 ? -6 : 1);
  const currMonday = new Date(d.getFullYear(), d.getMonth(), diffToMonday, 0, 0, 0);
  
  const lwStart = new Date(currMonday.getFullYear(), currMonday.getMonth(), currMonday.getDate() - 7, 0, 0, 0);
  const lwEnd = new Date(lwStart.getFullYear(), lwStart.getMonth(), lwStart.getDate() + 6, 23, 59, 59, 999);
  
  const m1 = String(lwStart.getMonth() + 1).padStart(2, '0');
  const d1 = String(lwStart.getDate()).padStart(2, '0');
  const m2 = String(lwEnd.getMonth() + 1).padStart(2, '0');
  const d2 = String(lwEnd.getDate()).padStart(2, '0');
  
  return {
    startTime: lwStart.getTime(),
    endTime: lwEnd.getTime(),
    label: `${m1}/${d1} – ${m2}/${d2}`,
    startDateYMD: toYMD(lwStart),
    endDateYMD: toYMD(lwEnd)
  };
};

const SESSION_STATE_KEY = 'onboarding_report_filters_v3';
const SESSION_DATA_KEY = 'onboarding_report_cached_data_v3';

const getSavedSessionState = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_STATE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
};

const getSavedSessionData = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_DATA_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const OnboardingReport = () => {
  const savedState = useMemo(() => getSavedSessionState(), []);
  const cachedData = useMemo(() => getSavedSessionData(), []);

  // Initialize data with cached data or bundled default checklist dataset
  const [data, setData] = useState(cachedData || defaultOnboardingData || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportDate, setReportDate] = useState(savedState.reportDate || '11/04/2026');
  const [syncNotice, setSyncNotice] = useState(null);

  // Filters State matching Image 2
  const [quickSelect, setQuickSelect] = useState(savedState.quickSelect || 'All');
  const [startDate, setStartDate] = useState(savedState.startDate || '');
  const [endDate, setEndDate] = useState(savedState.endDate || '');
  const [clientFilter, setClientFilter] = useState(savedState.clientFilter || '');
  const [supplierFilter, setSupplierFilter] = useState(savedState.supplierFilter || '');
  const [entityFilter, setEntityFilter] = useState(savedState.entityFilter || '');
  const [statusFilter, setStatusFilter] = useState(savedState.statusFilter || '');
  const [search, setSearch] = useState(savedState.search || '');

  // Sorting
  const [sortKey, setSortKey] = useState(savedState.sortKey || 'status');
  const [sortDir, setSortDir] = useState(savedState.sortDir !== undefined ? savedState.sortDir : 1);

  // Pagination State for Main Table
  const [mainPage, setMainPage] = useState(savedState.mainPage || 1);
  const [mainPageSize, setMainPageSize] = useState(savedState.mainPageSize || 10);

  // Pagination State for BGC Initiated Last Week Table
  const [recentPage, setRecentPage] = useState(savedState.recentPage || 1);
  const [recentPageSize, setRecentPageSize] = useState(savedState.recentPageSize || 10);

  // Reference Last Week Window
  const priorWeek = useMemo(() => getPriorWeekWindow(new Date()), []);

  // Calculate Date Ranges for Quick Select Presets
  const calculateDateRange = (preset) => {
    const today = new Date();
    let start = new Date(today);
    let end = new Date(today);

    if (preset === 'All') {
      return { startDate: '', endDate: '' };
    } else if (preset === 'Today') {
      start = new Date(today);
      end = new Date(today);
    } else if (preset === 'Yesterday') {
      start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
      end = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
    } else if (preset === 'This Week') {
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Monday
      start = new Date(today.getFullYear(), today.getMonth(), diff);
      end = new Date(today);
    } else if (preset === 'Last Week') {
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1) - 7;
      start = new Date(today.getFullYear(), today.getMonth(), diff);
      end = new Date(today.getFullYear(), today.getMonth(), diff + 6);
    } else if (preset === 'This Month') {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today);
    } else if (preset === 'Last Month') {
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
    } else if (preset === 'This Quarter') {
      const quarter = Math.floor(today.getMonth() / 3);
      start = new Date(today.getFullYear(), quarter * 3, 1);
      end = new Date(today);
    } else if (preset === 'Last Quarter') {
      const quarter = Math.floor(today.getMonth() / 3) - 1;
      start = new Date(today.getFullYear(), quarter * 3, 1);
      end = new Date(today.getFullYear(), (quarter + 1) * 3, 0);
    }
    return { startDate: toYMD(start), endDate: toYMD(end) };
  };

  const handleQuickSelectChange = (e) => {
    const val = e.target.value;
    setQuickSelect(val);
    if (val === 'Custom') return;

    if (val === 'All') {
      setStartDate('');
      setEndDate('');
      return;
    }

    const { startDate: newStart, endDate: newEnd } = calculateDateRange(val);
    setStartDate(newStart);
    setEndDate(newEnd);
  };

  // Reset page when filters change
  const isFirstMount = useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    setMainPage(1);
  }, [search, statusFilter, clientFilter, supplierFilter, entityFilter, startDate, endDate]);

  // Persist state to sessionStorage
  useEffect(() => {
    try {
      const stateToPersist = {
        quickSelect,
        startDate,
        endDate,
        clientFilter,
        supplierFilter,
        entityFilter,
        statusFilter,
        search,
        sortKey,
        sortDir,
        reportDate,
        mainPage,
        mainPageSize,
        recentPage,
        recentPageSize
      };
      sessionStorage.setItem(SESSION_STATE_KEY, JSON.stringify(stateToPersist));
    } catch (e) {
      console.warn('Could not save state to sessionStorage:', e);
    }
  }, [quickSelect, startDate, endDate, clientFilter, supplierFilter, entityFilter, statusFilter, search, sortKey, sortDir, reportDate, mainPage, mainPageSize, recentPage, recentPageSize]);

  // Dynamic dropdown options extracted from current dataset
  const clientOptions = useMemo(() => {
    return Array.from(new Set(data.map(r => r.client).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const supplierOptions = useMemo(() => {
    return Array.from(new Set(data.map(r => r.supplier).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const entityOptions = useMemo(() => {
    const fromData = Array.from(new Set(data.map(r => r.entity).filter(Boolean)));
    const standards = ['C2C', 'W2', 'US - Prophecy', 'Offshore - Prophecy'];
    return Array.from(new Set([...standards, ...fromData])).filter(Boolean);
  }, [data]);

  // Fetch live API data and merge with Excel dataset
  const fetchLiveData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const res = await axios.get(`${BASE_URL}/api/onboarding/queue`, { headers });
      if (res.data && res.data.success && Array.isArray(res.data.data) && res.data.data.length > 0) {
        // Map live database records and harmonize with Excel dataset by candidate name
        const excelMap = new Map();
        data.forEach(item => {
          if (item.name) excelMap.set(item.name.toLowerCase().trim(), item);
        });

        const liveMerged = res.data.data.map(item => {
          let st = 'Onboarding In Progress';
          const rawCode = (item.StatusCode || '').toUpperCase();
          const stageCode = (item.CurrentStageCode || '').toUpperCase();

          if (rawCode === 'COMPLETED' || stageCode === 'ONBOARDING_COMPLETED') {
            st = item.ResultDeploymentId ? 'Started' : 'BGC Completed';
          } else if (rawCode === 'IN_PROGRESS' || stageCode === 'ONBOARDING_INITIATED') {
            st = (item.CompletedSteps && item.CompletedSteps > 0) ? 'BGC In Progress' : 'BGC Initiated';
          } else if (rawCode === 'INITIATED') {
            st = 'BGC Initiated';
          } else if (rawCode === 'HOLD' || rawCode === 'ON_HOLD') {
            st = 'Hold';
          } else if (rawCode === 'DROPPED' || rawCode === 'CANCELLED' || rawCode === 'REJECTED') {
            st = 'Dropped';
          } else if (rawCode === 'OFFER_ACCEPTED' || stageCode === 'OFFER_ACCEPTED' || stageCode === 'READY_FOR_CLIENT') {
            st = 'Offer in Progress';
          }

          const candFullName = item.CandidateFullName || 
            item.CandidateName || 
            (item.CandidateFirstName ? `${item.CandidateFirstName} ${item.CandidateLastName || ''}`.trim() : 'Candidate');

          // Enrich with Excel record if available
          const xlMatch = excelMap.get(candFullName.toLowerCase().trim());

          let commentText = '';
          if (item.Notes && item.Notes.trim()) {
            commentText = item.Notes.trim();
          } else if (xlMatch?.comment) {
            commentText = xlMatch.comment;
          } else if (item.JobTitle) {
            commentText = item.JobTitle;
            if (item.ContractorCompanyName) {
              commentText += ` · ${item.ContractorCompanyName}`;
            }
          }

          return {
            id: item.OnboardingInstanceId || xlMatch?.id,
            submissionId: item.SubmissionId,
            name: candFullName,
            client: item.ClientCompanyName || item.ClientName || xlMatch?.client || 'Direct',
            end: item.EndClientName || xlMatch?.end || '',
            confirmed: formatDate(item.InitiatedAtUtc || item.CreatedAtUtc || xlMatch?.confirmed),
            bgcInitiated: xlMatch?.bgcInitiated || formatDate(item.InitiatedAtUtc),
            startDate: formatDate(item.DeploymentStartDate) || xlMatch?.startDate || '',
            status: xlMatch?.status || st,
            comment: commentText,
            jobTitle: item.JobTitle || xlMatch?.role || '',
            entity: item.EmploymentType || xlMatch?.entity || 'C2C',
            supplier: item.ContractorCompanyName || xlMatch?.supplier || 'Prophecy Consulting Inc',
            flag: rawCode === 'HOLD' || rawCode === 'DROPPED' || stageCode === 'REJECTED' || xlMatch?.flag,
            totalSteps: item.TotalSteps || 0,
            completedSteps: item.CompletedSteps || 0,
            raw: item
          };
        });

        // Also append any Excel candidates not present in live DB
        const liveNames = new Set(liveMerged.map(r => r.name.toLowerCase().trim()));
        data.forEach(r => {
          if (!liveNames.has(r.name.toLowerCase().trim())) {
            liveMerged.push(r);
          }
        });

        setData(liveMerged);
        setSyncNotice(`Synced with live database (${liveMerged.length} total candidates)`);
        try {
          sessionStorage.setItem(SESSION_DATA_KEY, JSON.stringify(liveMerged));
        } catch (e) {
          console.warn('Could not cache live data in sessionStorage:', e);
        }
      } else {
        setSyncNotice('Connected to live API. Using verified Onboarding Checklist dataset.');
      }
    } catch (err) {
      console.warn('Live API sync notice:', err.message);
      setSyncNotice('Operating in offline/verified mode using Onboarding Checklist dataset.');
    } finally {
      setLoading(false);
      setTimeout(() => setSyncNotice(null), 5000);
    }
  }, [data]);

  // Handle client-side Excel upload / sync
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary', cellDates: true });
        const parsed = parseOnboardingChecklistWorkbook(wb);
        if (parsed && parsed.length > 0) {
          setData(parsed);
          setSyncNotice(`Successfully loaded ${parsed.length} candidates from ${file.name}`);
          try {
            sessionStorage.setItem(SESSION_DATA_KEY, JSON.stringify(parsed));
          } catch (err) {
            console.warn('Could not cache uploaded data:', err);
          }
        } else {
          setError('Could not find candidate rows in the uploaded workbook sheets.');
        }
      } catch (err) {
        console.error('Error parsing uploaded Excel file:', err);
        setError(`Failed to parse Excel file: ${err.message}`);
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = '';
  };

  // Helper to check if candidate's BGC was initiated in the prior week
  const isCandidateBgcInitiatedLastWeek = useCallback((cand) => {
    if (!cand.bgcInitiated) return false;
    const t = parseDate(cand.bgcInitiated);
    return t >= priorWeek.startTime && t <= priorWeek.endTime;
  }, [priorWeek]);

  // Sorting Handler
  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(prev => prev * -1);
    } else {
      setSortKey(key);
      setSortDir(1);
    }
  };

  // Filter Data matching all filters from Image 2
  const filteredData = useMemo(() => {
    const q = search.toLowerCase().trim();
    return data.filter(r => {
      // Status Filter
      if (statusFilter) {
        if (statusFilter === 'flagged') {
          if (!(r.flag || r.status === 'Dropped' || r.status === 'Hold')) return false;
        } else if (statusFilter === 'In Progress') {
          if (r.status !== 'In Progress' && r.status !== 'BGC In Progress' && r.status !== 'Onboarding In Progress' && r.status !== 'BGC Initiated') return false;
        } else if (r.status !== statusFilter) {
          return false;
        }
      }

      // Client Filter
      if (clientFilter && (r.client || '') !== clientFilter) {
        return false;
      }

      // Supplier Filter
      if (supplierFilter && (r.supplier || '') !== supplierFilter) {
        return false;
      }

      // Entity Filter
      if (entityFilter) {
        const ent = (r.entity || '').toUpperCase();
        const filt = entityFilter.toUpperCase();
        if (filt.includes('US') && !ent.includes('US') && ent !== 'W2') return false;
        if (filt.includes('OFFSHORE') && !ent.includes('OFFSHORE')) return false;
        if (filt === 'C2C' && ent !== 'C2C') return false;
        if (filt === 'W2' && ent !== 'W2') return false;
      }

      // Date Range Filter (inspects bgcInitiated date, confirmed date, or start date)
      if (startDate || endDate) {
        const candDateStr = r.bgcInitiated || r.confirmed || r.startDate;
        if (!candDateStr) return false;
        const candTime = parseDate(candDateStr);
        if (!candTime) return false;
        const candYMD = toYMD(new Date(candTime));

        if (startDate && candYMD < startDate) return false;
        if (endDate && candYMD > endDate) return false;
      }

      // Search Query
      if (q) {
        const hay = `${r.name} ${r.client} ${r.end} ${r.comment} ${r.status} ${r.jobTitle || ''} ${r.supplier || ''} ${r.entity || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }

      return true;
    });
  }, [data, search, statusFilter, clientFilter, supplierFilter, entityFilter, startDate, endDate]);

  // Sorted rows
  const sortedData = useMemo(() => {
    const rows = [...filteredData];
    rows.sort((a, b) => {
      let av, bv;
      if (sortKey === 'confirmed') {
        av = parseDate(a.confirmed);
        bv = parseDate(b.confirmed);
      } else if (sortKey === 'bgcInitiated') {
        av = parseDate(a.bgcInitiated);
        bv = parseDate(b.bgcInitiated);
      } else {
        av = (a[sortKey] || '').toLowerCase();
        bv = (b[sortKey] || '').toLowerCase();
      }
      return av < bv ? -1 * sortDir : av > bv ? 1 * sortDir : 0;
    });
    return rows;
  }, [filteredData, sortKey, sortDir]);

  // Dynamic KPI Metrics calculated from dataset
  const total = data.length;
  const clientCount = new Set(data.map(r => r.client || 'Direct')).size;

  // BGC Initiated Last Week calculation depends on XLSX bgcInitiated date
  const bgcInitiatedLWRows = useMemo(() => {
    return data.filter(r => isCandidateBgcInitiatedLastWeek(r))
               .sort((a, b) => parseDate(b.bgcInitiated) - parseDate(a.bgcInitiated));
  }, [data, isCandidateBgcInitiatedLastWeek]);
  const bgcInitiatedLWCount = bgcInitiatedLWRows.length;

  const startedCount = data.filter(r => r.status === 'Started').length;
  const receivedStartDateCount = data.filter(r => r.status === 'Received Start Date').length;
  const completedCount = data.filter(r => r.status === 'BGC Completed' || r.status === 'Onboarding Completed').length;
  const inProgCount = data.filter(r => r.status === 'BGC In Progress').length;
  const offerCount = data.filter(r => r.status === 'Offer in Progress').length;
  const onboardingInProgCount = data.filter(r => r.status === 'Onboarding In Progress' || r.status === 'BGC Initiated').length;

  // Dynamic list of unique clients for header subtitle
  const clientSummaryText = useMemo(() => {
    const names = Array.from(new Set(data.map(r => r.client).filter(Boolean)));
    if (names.length === 0) return 'No clients';
    if (names.length <= 6) return names.join(' · ');
    return `${names.slice(0, 5).join(' · ')} · +${names.length - 5} more`;
  }, [data]);

  // Paginated Data for Main Table
  const totalMainRows = sortedData.length;
  const totalMainPages = Math.max(1, Math.ceil(totalMainRows / mainPageSize));
  const safeMainPage = Math.min(mainPage, totalMainPages);
  const mainStartIndex = (safeMainPage - 1) * mainPageSize;
  const mainEndIndex = Math.min(mainStartIndex + mainPageSize, totalMainRows);
  const paginatedMainData = useMemo(() => {
    return sortedData.slice(mainStartIndex, mainStartIndex + mainPageSize);
  }, [sortedData, mainStartIndex, mainPageSize]);

  // Paginated Data for BGC Initiated Last Week Table
  const totalRecentRows = bgcInitiatedLWRows.length;
  const totalRecentPages = Math.max(1, Math.ceil(totalRecentRows / recentPageSize));
  const safeRecentPage = Math.min(recentPage, totalRecentPages);
  const recentStartIndex = (safeRecentPage - 1) * recentPageSize;
  const recentEndIndex = Math.min(recentStartIndex + recentPageSize, totalRecentRows);
  const paginatedRecentData = useMemo(() => {
    return bgcInitiatedLWRows.slice(recentStartIndex, recentStartIndex + recentPageSize);
  }, [bgcInitiatedLWRows, recentStartIndex, recentPageSize]);

  // Truncate comment/detail to ~50 characters with tooltip for full text
  const truncateDetail = (str, limit = 50) => {
    if (!str || str === '—') {
      return { text: '—', isTruncated: false, fullText: '' };
    }
    const cleanStr = String(str).trim();
    if (cleanStr.length <= limit) {
      return { text: cleanStr, isTruncated: false, fullText: cleanStr };
    }
    const slice = cleanStr.slice(0, limit);
    const lastSpace = slice.lastIndexOf(' ');
    const cut = lastSpace > 35 ? slice.slice(0, lastSpace) : slice;
    return {
      text: cut.trim() + '…',
      isTruncated: true,
      fullText: cleanStr
    };
  };

  // Render pagination bar helper
  // Smart windowed pagination numbers generator (e.g. [1, 2, 3, 4, 5, '...', 20])
  const getPaginationItems = (currentPage, totalPages) => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (currentPage <= 4) {
      return [1, 2, 3, 4, 5, '...', totalPages];
    }
    if (currentPage >= totalPages - 3) {
      return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }
    return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
  };

  const renderPagination = (currentPage, totalPages, totalRows, startIndex, endIndex, pageSize, onPageChange, onPageSizeChange) => {
    if (totalRows === 0) return null;

    return (
      <div className="table-pagination-bar">
        <div className="pagination-left">
          <div className="page-size-selector">
            <span>Show</span>
            <select 
              value={pageSize} 
              onChange={(e) => {
                onPageSizeChange(Number(e.target.value));
                onPageChange(1);
              }}
              className="per-page-select"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
            <span>per page</span>
          </div>
          <span className="pagination-info">
            Showing <strong>{startIndex + 1}</strong>–<strong>{endIndex}</strong> of <strong>{totalRows}</strong> candidates
          </span>
        </div>
        <div className="pagination-controls">
          <button 
            type="button"
            className="page-btn" 
            onClick={() => onPageChange(currentPage - 1)} 
            disabled={currentPage <= 1}
            title="Previous page"
          >
            <ChevronLeft size={15} />
            <span>Prev</span>
          </button>

          <div className="page-numbers">
            {getPaginationItems(currentPage, totalPages).map((item, idx) => {
              if (item === '...') {
                return (
                  <span key={`ellipsis-${idx}`} className="pagination-ellipsis">
                    …
                  </span>
                );
              }
              return (
                <button
                  key={item}
                  type="button"
                  className={`page-num-btn ${item === currentPage ? 'active' : ''}`}
                  onClick={() => onPageChange(item)}
                >
                  {item}
                </button>
              );
            })}
          </div>

          <button 
            type="button"
            className="page-btn" 
            onClick={() => onPageChange(currentPage + 1)} 
            disabled={currentPage >= totalPages}
            title="Next page"
          >
            <span>Next</span>
            <ChevronRight size={15} />
          </button>
        </div>
      </div>
    );
  };

  // Pipeline by Status Breakdown
  const statusOrder = [
    "Started", "Received Start Date", "BGC Completed", "BGC Initiated", 
    "BGC In Progress", "Offer in Progress", "Onboarding In Progress", 
    "Onboarding Completed", "Yet to Finalize", "Hold", "Dropped"
  ];
  const statusCounts = useMemo(() => {
    const counts = {};
    data.forEach(r => { counts[r.status] = (counts[r.status] || 0) + 1; });
    return counts;
  }, [data]);
  const maxStatusCount = Math.max(1, ...Object.values(statusCounts));

  // Risk items from real data
  const riskItems = useMemo(() => {
    return data.filter(x => x.flag || x.status === 'Dropped' || x.status === 'Hold');
  }, [data]);

  // End-Client Concentration from real data
  const clientConcentration = useMemo(() => {
    const counts = {};
    data.forEach(r => {
      const e = r.end ? r.end : `${r.client} (Direct)`;
      counts[e] = (counts[e] || 0) + 1;
    });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    const max = Math.max(1, ...sorted.map(s => s[1]));
    return { sorted, max };
  }, [data]);

  // CSV Export
  const exportCSV = () => {
    const head = [
      "Report Date", "Candidate Name", "Client", "End Client", 
      "Confirmed Date", "BGC Initiated Date", "Supplier", "Entity", 
      "BGC Status", "Comments / Role", "Risk Flag"
    ];
    const lines = [head.join(",")];
    filteredData.forEach(r => {
      const cells = [
        reportDate,
        r.name,
        r.client || "Direct",
        r.end || "",
        r.confirmed || "",
        r.bgcInitiated || "",
        r.supplier || "",
        r.entity || "",
        r.status,
        r.comment || r.jobTitle || "",
        r.flag ? "YES" : ""
      ];
      lines.push(cells.map(c => `"${String(c).replace(/"/g, '""')}"`).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Onboarding_Report_${reportDate.replace(/\//g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Scroll to confirmed section
  const scrollToConfirmed = () => {
    const el = document.getElementById('confirmedSection');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="onboarding-report-wrapper">
      <div className="wrap">
        {/* Modern Header */}
        <header style={{ marginBottom: '24px' }}>
          <div className="title-block">
            <div className="eyebrow">
              <span className="live-dot"></span>
              <Layers size={13} />
              HRM · OPERATIONS WORKSTATION
            </div>
            <h1>Onboarding Report</h1>
          </div>
        </header>

        {/* Loading / Error Banner */}
        {loading && (
          <div style={{ padding: '16px 20px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '14px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px', color: '#3b5bfd', fontWeight: '600' }}>
            <RotateCw size={18} className="spin" />
            <span>Syncing live onboarding candidates from database…</span>
          </div>
        )}

        {error && !loading && (
          <div style={{ padding: '16px 20px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '14px', marginBottom: '24px', color: '#dc2626', display: 'flex', alignItems: 'center', gap: '12px', fontWeight: '600' }}>
            <AlertTriangle size={18} />
            <span>{error}</span>
          </div>
        )}

        {/* KPI Cards Strip (Image 3 First 2 Cards + HTML Cards Suite) */}
        <div className="kpis">
          {/* Card 1: TOTAL CANDIDATES (Retained from Image 3) */}
          <div 
            className="modern-kpi kpi-blue" 
            role="button" 
            tabIndex="0" 
            style={{ cursor: 'pointer' }}
            onClick={() => setStatusFilter('')}
            title="Show all candidates"
          >
            <div className="kpi-top-row">
              <span className="lbl">Total Candidates</span>
              <div className="kpi-icon-badge" style={{ background: '#eff6ff', color: '#2563eb' }}>
                <Users size={16} />
              </div>
            </div>
            <div className="num">{total}</div>
            <div className="kpi-sub-lbl">Active in system</div>
          </div>

          {/* Card 2: CLIENTS ACTIVE (Retained from Image 3) */}
          <div className="modern-kpi kpi-purple">
            <div className="kpi-top-row">
              <span className="lbl">Clients Active</span>
              <div className="kpi-icon-badge" style={{ background: '#f5f3ff', color: '#7c3aed' }}>
                <Building2 size={16} />
              </div>
            </div>
            <div className="num">{clientCount}</div>
            <div className="kpi-sub-lbl">{clientSummaryText.length > 22 ? `${clientSummaryText.slice(0, 22)}…` : clientSummaryText}</div>
          </div>

          {/* Card 3: BGC INITIATED LAST WEEK (From HTML & Excel BGC Initiated Date) */}
          <div 
            className="modern-kpi kpi-cyan" 
            role="button" 
            tabIndex="0" 
            style={{ cursor: 'pointer' }}
            onClick={scrollToConfirmed}
            title="Click to jump to BGC Initiated Last Week table"
          >
            <div className="kpi-top-row">
              <span className="lbl">BGC Initiated Last Week</span>
              <div className="kpi-icon-badge" style={{ background: '#ecfeff', color: '#0891b2' }}>
                <Calendar size={16} />
              </div>
            </div>
            <div className="num" style={{ display: 'flex', alignItems: 'baseline', gap: '6px', color: '#0891b2' }}>
              {bgcInitiatedLWCount}
              <span style={{ fontSize: '11px', color: '#0891b2', fontWeight: '700' }}>↓ view</span>
            </div>
            <div className="kpi-sub-lbl">{priorWeek.label}</div>
          </div>

          {/* Card 4: STARTED (From HTML) */}
          <div 
            className="modern-kpi kpi-emerald"
            role="button"
            tabIndex="0"
            style={{ cursor: 'pointer' }}
            onClick={() => setStatusFilter(statusFilter === 'Started' ? '' : 'Started')}
            title="Filter by Started"
          >
            <div className="kpi-top-row">
              <span className="lbl">Started</span>
              <div className="kpi-icon-badge" style={{ background: '#ecfdf5', color: '#059669' }}>
                <Rocket size={16} />
              </div>
            </div>
            <div className="num" style={{ color: '#059669' }}>{startedCount}</div>
            <div className="kpi-sub-lbl">Deployed & active</div>
          </div>

          {/* Card 5: RECEIVED START DATE (From HTML) */}
          <div 
            className="modern-kpi kpi-gold"
            role="button"
            tabIndex="0"
            style={{ cursor: 'pointer' }}
            onClick={() => setStatusFilter(statusFilter === 'Received Start Date' ? '' : 'Received Start Date')}
            title="Filter by Received Start Date"
          >
            <div className="kpi-top-row">
              <span className="lbl">Received Start Date</span>
              <div className="kpi-icon-badge" style={{ background: '#fffbeb', color: '#d97706' }}>
                <CalendarCheck size={16} />
              </div>
            </div>
            <div className="num" style={{ color: '#d97706' }}>{receivedStartDateCount}</div>
            <div className="kpi-sub-lbl">Ready to deploy</div>
          </div>

          {/* Card 6: BGC COMPLETED (From HTML) */}
          <div 
            className="modern-kpi kpi-green"
            role="button"
            tabIndex="0"
            style={{ cursor: 'pointer' }}
            onClick={() => setStatusFilter(statusFilter === 'BGC Completed' ? '' : 'BGC Completed')}
            title="Filter by BGC Completed"
          >
            <div className="kpi-top-row">
              <span className="lbl">BGC Completed</span>
              <div className="kpi-icon-badge" style={{ background: '#f0fdf4', color: '#16a34a' }}>
                <ShieldCheck size={16} />
              </div>
            </div>
            <div className="num" style={{ color: '#16a34a' }}>{completedCount}</div>
            <div className="kpi-sub-lbl">Clear & ready</div>
          </div>

          {/* Card 7: BGC IN PROGRESS (From HTML) */}
          <div 
            className="modern-kpi kpi-amber"
            role="button"
            tabIndex="0"
            style={{ cursor: 'pointer' }}
            onClick={() => setStatusFilter(statusFilter === 'BGC In Progress' ? '' : 'BGC In Progress')}
            title="Filter by BGC In Progress"
          >
            <div className="kpi-top-row">
              <span className="lbl">BGC In Progress</span>
              <div className="kpi-icon-badge" style={{ background: '#f0f9ff', color: '#0284c7' }}>
                <Hourglass size={16} />
              </div>
            </div>
            <div className="num" style={{ color: '#0284c7' }}>{inProgCount}</div>
            <div className="kpi-sub-lbl">Verification active</div>
          </div>

          {/* Card 8: OFFER IN PROGRESS (From HTML) */}
          <div 
            className="modern-kpi kpi-pink"
            role="button"
            tabIndex="0"
            style={{ cursor: 'pointer' }}
            onClick={() => setStatusFilter(statusFilter === 'Offer in Progress' ? '' : 'Offer in Progress')}
            title="Filter by Offer in Progress"
          >
            <div className="kpi-top-row">
              <span className="lbl">Offer in Progress</span>
              <div className="kpi-icon-badge" style={{ background: '#fdf2f8', color: '#db2777' }}>
                <Sparkles size={16} />
              </div>
            </div>
            <div className="num" style={{ color: '#db2777' }}>{offerCount}</div>
            <div className="kpi-sub-lbl">Awaiting acceptance</div>
          </div>

          {/* Card 9: ONBOARDING IN PROGRESS (From HTML) */}
          <div 
            className="modern-kpi kpi-teal"
            role="button"
            tabIndex="0"
            style={{ cursor: 'pointer' }}
            onClick={() => setStatusFilter(statusFilter === 'Onboarding In Progress' ? '' : 'Onboarding In Progress')}
            title="Filter by Onboarding In Progress"
          >
            <div className="kpi-top-row">
              <span className="lbl">Onboarding In Progress</span>
              <div className="kpi-icon-badge" style={{ background: '#f0fdfa', color: '#0d9488' }}>
                <Layers size={16} />
              </div>
            </div>
            <div className="num" style={{ color: '#0d9488' }}>{onboardingInProgCount}</div>
            <div className="kpi-sub-lbl">Document workflow</div>
          </div>
        </div>

        {/* Dedicated 2-Row Filter Controls Card (matching Reference Image) */}
        <div className="date-filters-card">
          <div className="date-filters-title">Date filters:</div>

          {/* Row 1: Quick Select, Start Date, End Date, Client, Supplier, Entity */}
          <div className="date-filters-grid-row1">
            <div className="date-filter-field">
              <label className="date-filter-label">Quick Select</label>
              <select 
                value={quickSelect} 
                onChange={handleQuickSelectChange}
                className="date-filter-control"
              >
                <option value="All">All Dates</option>
                <option value="Today">Today</option>
                <option value="Yesterday">Yesterday</option>
                <option value="This Week">This Week</option>
                <option value="Last Week">Last Week</option>
                <option value="This Month">This Month</option>
                <option value="Last Month">Last Month</option>
                <option value="This Quarter">This Quarter</option>
                <option value="Last Quarter">Last Quarter</option>
                <option value="Custom">Custom</option>
              </select>
            </div>

            <div className="date-filter-field">
              <label className="date-filter-label">Start date</label>
              <input 
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setQuickSelect(e.target.value || endDate ? 'Custom' : 'All');
                }}
                className="date-filter-control"
              />
            </div>

            <div className="date-filter-field">
              <label className="date-filter-label">End date</label>
              <input 
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setQuickSelect(startDate || e.target.value ? 'Custom' : 'All');
                }}
                className="date-filter-control"
              />
            </div>

            <div className="date-filter-field">
              <label className="date-filter-label">Client</label>
              <select 
                className="date-filter-control"
                value={clientFilter}
                onChange={(e) => setClientFilter(e.target.value)}
              >
                <option value="">All Clients</option>
                {clientOptions.map((client) => (
                  <option key={client} value={client}>{client}</option>
                ))}
              </select>
            </div>

            <div className="date-filter-field">
              <label className="date-filter-label">Supplier</label>
              <select 
                className="date-filter-control"
                value={supplierFilter}
                onChange={(e) => setSupplierFilter(e.target.value)}
              >
                <option value="">All Suppliers</option>
                {supplierOptions.map((supplier) => (
                  <option key={supplier} value={supplier}>{supplier}</option>
                ))}
              </select>
            </div>

            <div className="date-filter-field">
              <label className="date-filter-label">Entity</label>
              <select 
                className="date-filter-control"
                value={entityFilter}
                onChange={(e) => setEntityFilter(e.target.value)}
              >
                <option value="">All Entities</option>
                {entityOptions.map((entity) => (
                  <option key={entity} value={entity}>{entity}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 2: Status & Search */}
          <div className="date-filters-grid-row2">
            <div className="date-filter-field" style={{ width: '165px', flexShrink: 0 }}>
              <label className="date-filter-label">Status</label>
              <select 
                className="date-filter-control"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="In Progress">In Progress</option>
                <option value="Started">Started</option>
                <option value="Received Start Date">Received Start Date</option>
                <option value="BGC Completed">BGC Completed</option>
                <option value="BGC In Progress">BGC In Progress</option>
                <option value="BGC Initiated">BGC Initiated</option>
                <option value="Offer in Progress">Offer in Progress</option>
                <option value="Onboarding In Progress">Onboarding In Progress</option>
                <option value="Hold">Hold</option>
                <option value="Dropped">Dropped</option>
                <option value="flagged">⚠ Attention Flagged</option>
              </select>
            </div>

            <div className="date-filter-field" style={{ flex: 1 }}>
              <label className="date-filter-label">Search</label>
              <div className="date-filter-search-box">
                <Search size={16} color="#94a3b8" />
                <input
                  type="text"
                  placeholder="Search candidate name, job title, client..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button 
                    type="button"
                    onClick={() => setSearch('')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '16px', lineHeight: 1 }}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Grid: Left Table, Right Sidebars */}
        <div className="grid">
          <div>
            {/* Modern Table Card */}
            <div className="modern-table-card">
              <div className="table-responsive-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('name')}>
                        Candidate <span className="arr">{sortKey === 'name' ? (sortDir > 0 ? '▲' : '▼') : ''}</span>
                      </th>
                      <th onClick={() => handleSort('client')}>
                        Client / End Client <span className="arr">{sortKey === 'client' ? (sortDir > 0 ? '▲' : '▼') : ''}</span>
                      </th>
                      <th onClick={() => handleSort('confirmed')}>
                        Offer Confirmed <span className="arr">{sortKey === 'confirmed' ? (sortDir > 0 ? '▲' : '▼') : ''}</span>
                      </th>
                      <th onClick={() => handleSort('bgcInitiated')}>
                        BGC Initiated <span className="arr">{sortKey === 'bgcInitiated' ? (sortDir > 0 ? '▲' : '▼') : ''}</span>
                      </th>
                      <th onClick={() => handleSort('status')}>
                        BGC Status <span className="arr">{sortKey === 'status' ? (sortDir > 0 ? '▲' : '▼') : ''}</span>
                      </th>
                      <th>Supplier / Entity</th>
                      <th>Comments / Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedMainData.length > 0 ? (
                      paginatedMainData.map((r, idx) => {
                        const cls = STATUS_CLASS[r.status] || "st-fin";
                        const isDropped = r.status === 'Dropped';
                        const isFlagged = r.flag || isDropped || r.status === 'Hold';
                        const isBgcLW = isCandidateBgcInitiatedLastWeek(r);

                        return (
                          <tr key={idx} className={`${isFlagged ? 'flagged' : ''} ${isDropped ? 'dropped' : ''} ${isBgcLW ? 'bgc-lw-row' : ''}`}>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div 
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '8px',
                                    background: getAvatarBg(r.name),
                                    color: '#ffffff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px',
                                    fontWeight: '700',
                                    letterSpacing: '0.04em',
                                    flexShrink: 0,
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                                  }}
                                >
                                  {getInitials(r.name)}
                                </div>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontWeight: '700', color: '#0f172a', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                                    {r.name}
                                    {r.flag && <span className="flag-dot" title="Attention Flagged"></span>}
                                  </div>
                                  {r.raw?.CandidateEmail && (
                                    <div style={{ fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '160px' }}>
                                      {r.raw.CandidateEmail}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: '100px' }}>
                                <div style={{ fontWeight: '600', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12.5px' }}>
                                  <Building2 size={12} style={{ color: '#3b5bfd' }} />
                                  {r.client || 'Direct'}
                                </div>
                                {r.end ? (
                                  <div style={{ fontSize: '11px', color: '#475569', display: 'flex', alignItems: 'center', gap: '3px' }}>
                                    <span style={{ color: '#cbd5e1' }}>↳</span>
                                    <span style={{ background: '#f1f5f9', padding: '1px 5px', borderRadius: '4px', fontWeight: '500' }}>
                                      {r.end}
                                    </span>
                                  </div>
                                ) : (
                                  <div style={{ fontSize: '10.5px', color: '#94a3b8' }}>
                                    ↳ Direct
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: '#334155', fontWeight: '500', whiteSpace: 'nowrap' }}>
                                <Calendar size={12} style={{ color: '#94a3b8' }} />
                                {r.confirmed || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Pending</span>}
                              </div>
                            </td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#334155', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                {r.bgcInitiated ? (
                                  <>
                                    <span>{r.bgcInitiated}</span>
                                    {isBgcLW && <span className="lw-badge">last wk</span>}
                                  </>
                                ) : (
                                  <span style={{ color: '#94a3b8', fontStyle: 'italic', fontWeight: '400' }}>—</span>
                                )}
                              </div>
                            </td>
                            <td>
                              <span className={`pill ${cls}`}>{r.status}</span>
                            </td>
                            <td>
                              <div style={{ fontSize: '12px', color: '#334155', fontWeight: '500', minWidth: '95px' }}>
                                {r.supplier || '—'}
                              </div>
                              {r.entity && (
                                <span style={{ fontSize: '10px', padding: '1px 5px', borderRadius: '3px', background: '#f1f5f9', color: '#64748b', fontWeight: '600', display: 'inline-block', marginTop: '2px' }}>
                                  {r.entity}
                                </span>
                              )}
                            </td>
                            <td className="table-comment-cell">
                              {(() => {
                                const rawComment = r.comment || r.role || r.jobTitle || '—';
                                const { text: displayComment, isTruncated, fullText } = truncateDetail(rawComment, 50);
                                const headerLabel = (r.jobTitle && r.jobTitle !== fullText && r.jobTitle.length <= 40)
                                  ? r.jobTitle
                                  : (r.role && r.role !== fullText && r.role.length <= 40 ? r.role : 'Full Note & Details');

                                return (
                                  <div className={`comment-cell-content ${isTruncated ? 'has-tooltip' : ''}`}>
                                    <div className="comment-main-text">
                                      {displayComment}
                                      {isTruncated && <span className="more-indicator">[more]</span>}
                                    </div>
                                    {r.role && r.comment !== r.role && r.role.length <= 50 && (
                                      <div className="comment-role-text">
                                        {r.role}
                                      </div>
                                    )}
                                    {isTruncated && (
                                      <div className="comment-tooltip-card" role="tooltip">
                                        <div className="tooltip-header">
                                          <span>{headerLabel}</span>
                                        </div>
                                        <div className="tooltip-text">{fullText}</div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" style={{ padding: '36px', textAlign: 'center', color: '#94a3b8' }}>
                          {loading ? 'Loading candidates…' : 'No onboarding candidates matched the filter criteria.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {renderPagination(
                safeMainPage, 
                totalMainPages, 
                totalMainRows, 
                mainStartIndex, 
                mainEndIndex, 
                mainPageSize, 
                setMainPage, 
                setMainPageSize
              )}
            </div>

            {/* Dedicated BGC Initiated Last Week Section (Aligned directly under Main Table) */}
            <div className="modern-table-card" id="confirmedSection" style={{ marginTop: '20px', scrollMarginTop: '20px' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', background: '#f8fafc' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#ecfeff', color: '#0891b2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Calendar size={16} />
                  </div>
                  <div>
                    <strong style={{ fontSize: '14.5px', color: '#0f172a', display: 'block' }}>
                      BGC Initiated Last Week
                    </strong>
                    <span style={{ color: '#64748b', fontSize: '12px' }}>
                      {priorWeek.label} · candidates whose BGC was initiated in the prior week (from Onboarding Checklist)
                    </span>
                  </div>
                </div>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#0891b2', background: '#ecfeff', padding: '3px 10px', borderRadius: '12px' }}>
                  {bgcInitiatedLWCount} candidates
                </span>
              </div>
              <div className="table-responsive-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Candidate</th>
                      <th>Client / End Client</th>
                      <th>BGC Initiated</th>
                      <th>BGC Status</th>
                      <th>Supplier / Entity</th>
                      <th>Comments / Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRecentData.length > 0 ? (
                      paginatedRecentData.map((r, idx) => {
                        const cls = STATUS_CLASS[r.status] || "st-fin";
                        return (
                          <tr key={idx}>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div 
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '8px',
                                    background: getAvatarBg(r.name),
                                    color: '#ffffff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px',
                                    fontWeight: '700',
                                    flexShrink: 0
                                  }}
                                >
                                  {getInitials(r.name)}
                                </div>
                                <div>
                                  <span style={{ fontWeight: '700', color: '#0f172a', fontSize: '13px' }}>
                                    {r.name}
                                  </span>
                                  {r.flag && <span className="flag-dot" style={{ marginLeft: '6px' }}></span>}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div style={{ fontWeight: '600', color: '#0f172a' }}>{r.client || 'Direct'}</div>
                              <div style={{ color: '#64748b', fontSize: '11.5px' }}>↳ {r.end || 'Direct Placement'}</div>
                            </td>
                            <td>
                              <span style={{ fontSize: '12.5px', color: '#0891b2', fontWeight: '700' }}>{r.bgcInitiated}</span>
                            </td>
                            <td><span className={`pill ${cls}`}>{r.status}</span></td>
                            <td>
                              <div style={{ fontSize: '12px', color: '#334155', fontWeight: '500' }}>{r.supplier || '—'}</div>
                              {r.entity && (
                                <span style={{ fontSize: '10px', padding: '1px 5px', borderRadius: '3px', background: '#f1f5f9', color: '#64748b', fontWeight: '600' }}>
                                  {r.entity}
                                </span>
                              )}
                            </td>
                            <td className="table-comment-cell">
                              {(() => {
                                const rawComment = r.comment || r.role || '—';
                                const { text: displayComment, isTruncated, fullText } = truncateDetail(rawComment, 50);
                                const headerLabel = (r.jobTitle && r.jobTitle !== fullText && r.jobTitle.length <= 40)
                                  ? r.jobTitle
                                  : (r.role && r.role !== fullText && r.role.length <= 40 ? r.role : 'Full Note & Details');

                                return (
                                  <div className={`comment-cell-content ${isTruncated ? 'has-tooltip' : ''}`}>
                                    <div className="comment-main-text">
                                      {displayComment}
                                      {isTruncated && <span className="more-indicator">[more]</span>}
                                    </div>
                                    {r.role && r.comment !== r.role && r.role.length <= 50 && (
                                      <div className="comment-role-text">
                                        {r.role}
                                      </div>
                                    )}
                                    {isTruncated && (
                                      <div className="comment-tooltip-card" role="tooltip">
                                        <div className="tooltip-header">
                                          <span>{headerLabel}</span>
                                        </div>
                                        <div className="tooltip-text">{fullText}</div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="6" style={{ color: '#94a3b8', padding: '24px 20px', textAlign: 'center' }}>
                          No candidates with BGC initiated in this window.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {renderPagination(
                safeRecentPage, 
                totalRecentPages, 
                totalRecentRows, 
                recentStartIndex, 
                recentEndIndex, 
                recentPageSize, 
                setRecentPage, 
                setRecentPageSize
              )}
            </div>
          </div>

          {/* Right Side Panel */}
          <aside className="side">
            {/* Pipeline by Status Card */}
            <div className="modern-card">
              <div className="card-header">
                <span className="card-title">
                  <TrendingUp size={15} color="var(--teal-primary)" />
                  Pipeline by Status
                </span>
                <span style={{ fontSize: '11.5px', color: '#64748b', fontWeight: '600' }}>
                  {total} Total
                </span>
              </div>
              <div>
                {statusOrder.filter(s => statusCounts[s]).map((s, idx) => {
                  const c = statusCounts[s];
                  const pct = Math.round((c / maxStatusCount) * 100);
                  const color = STATUS_COLOR[s] || '#3b5bfd';
                  return (
                    <div 
                      key={idx} 
                      className="bar-row"
                      style={{ cursor: 'pointer' }}
                      onClick={() => setStatusFilter(statusFilter === s ? '' : s)}
                      title={`Filter by ${s}`}
                    >
                      <div className="bar-head">
                        <span style={{ fontWeight: statusFilter === s ? '700' : '500', color: statusFilter === s ? '#0f172a' : '#475569' }}>{s}</span>
                        <span className="v">{c}</span>
                      </div>
                      <div className="bar-track">
                        <div className="bar-fill" style={{ width: `${pct}%`, background: color }}></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Attention & Drops Card */}
            <div className="modern-card">
              <div className="card-header">
                <span className="card-title" style={{ color: riskItems.length > 0 ? '#dc2626' : '#0f172a' }}>
                  <AlertTriangle size={15} color={riskItems.length > 0 ? '#dc2626' : '#94a3b8'} />
                  Attention &amp; Drops
                </span>
                <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px', background: riskItems.length > 0 ? '#fef2f2' : '#f1f5f9', color: riskItems.length > 0 ? '#dc2626' : '#64748b', fontWeight: '700' }}>
                  {riskItems.length}
                </span>
              </div>
              <div>
                {riskItems.length > 0 ? (
                  riskItems.map((x, idx) => {
                    const dropped = x.status === 'Dropped';
                    const hold = x.status === 'Hold';
                    const reason = x.comment || (dropped ? 'Dropped from pipeline' : hold ? 'On hold with client' : 'Attention required');
                    const ico = dropped ? '✕' : hold ? '⏸' : '!';
                    const tagStyle = dropped 
                      ? { color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca' }
                      : hold
                      ? { color: '#d97706', background: '#fffbeb', border: '1px solid #fde68a' }
                      : { color: '#0284c7', background: '#f0f9ff', border: '1px solid #bae6fd' };

                    return (
                      <div key={idx} className="risk-item">
                        <span 
                          className="ico"
                          style={{
                            background: dropped ? '#fef2f2' : hold ? '#fffbeb' : '#f0f9ff',
                            color: dropped ? '#dc2626' : hold ? '#d97706' : '#0284c7'
                          }}
                        >
                          {ico}
                        </span>
                        <div>
                          <div className="rname">
                            {x.name}
                            {(dropped || hold) && (
                              <span 
                                style={{
                                  ...tagStyle,
                                  fontSize: '10px',
                                  fontWeight: '700',
                                  padding: '1px 6px',
                                  borderRadius: '4px',
                                  marginLeft: '6px'
                                }}
                              >
                                {dropped ? 'DROPPED' : 'HOLD'}
                              </span>
                            )}
                          </div>
                          <div className="rwhy">{reason}</div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="empty-risk-state">
                    <CheckCircle2 size={16} color="#3b5bfd" />
                    <span>All candidates on track · No blockers or drops</span>
                  </div>
                )}
              </div>
            </div>

            {/* End-Client Concentration Card */}
            <div className="modern-card">
              <div className="card-header">
                <span className="card-title">
                  <Building2 size={15} color="var(--teal-primary)" />
                  Client Concentration
                </span>
                <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>
                  Top Placements
                </span>
              </div>
              <div>
                {clientConcentration.sorted.slice(0, 10).map(([name, c], idx) => {
                  const pct = Math.round((c / clientConcentration.max) * 100);
                  return (
                    <div key={idx} className="bar-row">
                      <div className="bar-head">
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '700' }}>#{idx + 1}</span>
                          {name}
                        </span>
                        <span className="v">{c}</span>
                      </div>
                      <div className="bar-track">
                        <div 
                          className="bar-fill" 
                          style={{ 
                            width: `${pct}%`, 
                            background: idx === 0 ? 'linear-gradient(90deg, #3b5bfd, #2563eb)' : 'linear-gradient(90deg, #3b82f6, #0284c7)' 
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </aside>
        </div>

        {/* Modern Footer */}
        <footer>
          Generated {reportDate} · Prophecy / Cognifyar Onboarding Operations · Powered by Onboarding Checklist &amp; Live Database
        </footer>
      </div>
    </div>
  );
};

export default OnboardingReport;
